import React from 'react'
import { ChevronRightIcon } from 'lucide-react'
import { mobile } from './tokens'

interface MobileListRowProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  meta?: React.ReactNode
  leading?: React.ReactNode
  trailing?: React.ReactNode
  badges?: React.ReactNode
  onClick?: () => void
  /** Footer action strip below the main row */
  footer?: React.ReactNode
  chevron?: boolean
  className?: string
  disabled?: boolean
}

/**
 * Native-style tappable list row used across Jobs, Customers, Reviews, etc.
 */
export function MobileListRow({
  title,
  subtitle,
  meta,
  leading,
  trailing,
  badges,
  onClick,
  footer,
  chevron = true,
  className = '',
  disabled,
}: MobileListRowProps) {
  const content = (
    <div className="flex items-start gap-3">
      {leading && <div className="flex-shrink-0 mt-0.5">{leading}</div>}
      <div className="flex-1 min-w-0">
        {badges && <div className="flex items-center gap-2 flex-wrap mb-1">{badges}</div>}
        <div className="text-sm font-semibold text-gray-900 truncate">{title}</div>
        {subtitle != null && subtitle !== '' && (
          <div className="mt-0.5 text-xs text-gray-500 truncate">{subtitle}</div>
        )}
        {meta != null && meta !== '' && (
          <div className="mt-1.5 space-y-0.5 text-xs text-gray-600">{meta}</div>
        )}
      </div>
      {trailing}
      {chevron && onClick && !trailing && (
        <ChevronRightIcon className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
      )}
    </div>
  )

  return (
    <div className={`${mobile.surface} overflow-hidden ${className}`.trim()}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={`${mobile.row} disabled:opacity-50`}
        >
          {content}
        </button>
      ) : (
        <div className={mobile.row}>{content}</div>
      )}
      {footer && (
        <div className="flex border-t border-gray-100 divide-x divide-gray-100">{footer}</div>
      )}
    </div>
  )
}

interface MobileRowActionProps {
  children: React.ReactNode
  onClick: () => void
  tone?: 'default' | 'primary' | 'danger' | 'success' | 'warning'
  className?: string
}

const toneCls: Record<NonNullable<MobileRowActionProps['tone']>, string> = {
  default: 'text-gray-700 hover:bg-gray-50',
  primary: 'text-blue-700 hover:bg-blue-50',
  danger: 'text-red-600 hover:bg-red-50',
  success: 'text-green-700 hover:bg-green-50',
  warning: 'text-amber-800 bg-amber-50/50 hover:bg-amber-50',
}

export function MobileRowAction({
  children,
  onClick,
  tone = 'default',
  className = '',
}: MobileRowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 min-h-[44px] text-sm font-medium inline-flex items-center justify-center gap-1.5 ${toneCls[tone]} ${className}`.trim()}
    >
      {children}
    </button>
  )
}
